import { createApp } from 'vue';
import { AccountLink } from '@rei/account-flyout';
import { getUser } from '@rei/session';
import getAdobeTargetJsonOffer from '@rei/adobe-target-functions';
import AccountLinkFragment from './components/AccountLinkFragment.vue';
import LoadingFragment from './components/LoadingFragment.vue';
import AccountNavComponent from './components/AccountNavComponent.vue';
import AccountNavMobileComponent from './components/AccountNavMobileComponent.vue';
import AccountFlyoutComponent from './components/AccountFlyoutComponent.vue';
import SignInNudgeComponent from './components/SignInNudgeComponent.vue';

const gNav = document.querySelector('.gnav');
const isWhiteNav = gNav !== null;
const { clientWidth } = window.document.body;

let isAccountMounted = false;
let isSignInNudgeMounted = false;

let user = null;
let expiringRewards = null;
let signInNotificationExperience = 'control';
let zeroRewardsNavExperience = 'control';
let gugoExperience = 'control';
let expiringRewardsExperience = 'control';
let loginNonMemberExperience = 'control';
let nonMemberVariation = 'control';
let memberVariation = 'control';

const getExpiringRewards = async () => {
  const response = await fetch('/account-site/rewards-expiring', {
    method: 'GET',
    cache: 'no-cache',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
  });
  if (!response.ok) {
    return null;
  }
  const json = await response.json();
  return json;
};

if (isWhiteNav) {
  /* This is a default loading fragment that will display first and
    visually communicate to the user that something related to their
    account login is still pending.
  */
  createApp(LoadingFragment).mount('[data-ui="login-module-greeting"]');

  user = getUser()
    .then(async (userObj) => {
      user = userObj;
      const accountListItem = document.querySelector(
        '[data-ui="login-module-greeting-account-link"]',
      );
      if (accountListItem.hasAttribute('data-visible')) {
        accountListItem.removeAttribute('data-visible');
      }
      try {
        const zeroRewardsNavTest = await getAdobeTargetJsonOffer('zero-rewards-nav-test');
        if (zeroRewardsNavTest && zeroRewardsNavTest.length) {
          const [{ content }] = zeroRewardsNavTest;
          const [[{ experience }]] = content;
          zeroRewardsNavExperience = experience;
          const sentZeroRewardsMetric = sessionStorage.getItem('zeroRewardsSent');
          if (
            (!user || !user?.membership || !user.membership?.dividendBalance) &&
            !sentZeroRewardsMetric
          ) {
            window.metrics.click({
              eligibleTests: 'Accounts_ZeroRewardsNav',
            });
            sessionStorage.setItem('zeroRewardsSent', 'true');
          }
        }
      } catch (e) {
        console.error('Error with getAdobeTargetJsonOffer', e);
      }
      if (user && user.isLoggedIn) {
        if (user.membership) {
          try {
            expiringRewards = await getExpiringRewards();
            if (expiringRewards?.amountRemaining > 0) {
              const expiringRewardsTest = await getAdobeTargetJsonOffer(
                'expiring-rewards-nav-alert',
              );
              if (expiringRewardsTest && expiringRewardsTest.length) {
                const [{ content }] = expiringRewardsTest;
                const [[{ experience }]] = content;
                expiringRewardsExperience = experience;
                if (!localStorage.getItem('flyoutNotificationSeen')) {
                  window.metrics.link({ eligibleTests: 'Account_ExpiringRewards' });
                }
              }
            }
          } catch (e) {
            console.error('Error with getAdobeTargetJsonOffer', e);
          }
          if (user.membership.dividendBalance !== null) {
            try {
              const memberVariationsTest = await getAdobeTargetJsonOffer(
                'account-member-variations',
              );
              if (memberVariation && memberVariationsTest.length) {
                const [{ content }] = memberVariationsTest;
                const [[{ experience }]] = content;
                memberVariation = experience;
              }
            } catch (e) {
              console.error('Error with getAdobeTargetJsonOffer', e);
            }
          }
        } else {
          const loginNonMemberTest = await getAdobeTargetJsonOffer(
            'nav-alert-logged-in-non-members',
          );
          if (loginNonMemberTest && loginNonMemberTest.length) {
            const [{ content }] = loginNonMemberTest;
            const [[{ experience }]] = content;
            loginNonMemberExperience = experience;
            if (!localStorage.getItem('flyoutNotificationLoginNonMemberSeen')) {
              window.metrics.link({ eligibleTests: 'Account_LoginNonMember' });
            }
          }
          try {
            const nonMemberVariationsTest = await getAdobeTargetJsonOffer(
              'account-non-member-variations',
            );
            if (nonMemberVariationsTest && nonMemberVariationsTest.length) {
              const [{ content }] = nonMemberVariationsTest;
              const [[{ experience }]] = content;
              nonMemberVariation = experience;
            }
          } catch (e) {
            console.error('Error with getAdobeTargetJsonOffer', e);
          }
        }

        createApp(AccountNavComponent, {
          user,
          zeroRewardsNavExperience,
          expiringRewardsExperience,
          expiringRewards,
          loginNonMemberExperience,
        }).mount('[data-ui="login-module-greeting-account-link"]');
        createApp(AccountNavMobileComponent, {
          user,
          zeroRewardsNavExperience,
        }).mount('li[data-js="mobile-nav-login-link"]');
        const sentMemberViewMetric = sessionStorage.getItem('memberViewSent');
        if (
          window &&
          window.metrics &&
          user.membership &&
          !sentMemberViewMetric &&
          clientWidth < 992
        ) {
          window.metrics.click({
            siteId: 'global-sign-in',
            checkoutRewardsTotal: user.membership.dividendBalance || 0,
          });
          sessionStorage.setItem('memberViewSent', 'true');
        }
      } else {
        try {
          const signInNotificationTest = await getAdobeTargetJsonOffer(
            'navigation-alert-sign-in-mobile',
          );
          if (signInNotificationTest && signInNotificationTest.length) {
            const [{ content }] = signInNotificationTest;
            const [[{ experience }]] = content;
            signInNotificationExperience = experience;
          }
        } catch (e) {
          console.error('Error with getAdobeTargetJsonOffer', e);
        }
        try {
          const gugoTest = await getAdobeTargetJsonOffer('GUGO-signin-nudge');
          if (gugoTest && gugoTest.length) {
            const [{ content }] = gugoTest;
            const [[{ experience }]] = content;
            gugoExperience = experience;
          }
        } catch (e) {
          console.error('Error with getAdobeTargetJsonOffer', e);
        }
        createApp(AccountLinkFragment, {
          signInNotificationExperience,
          zeroRewardsNavExperience,
          gugoExperience,
        }).mount('[data-ui="login-module-greeting-account-link"]');
      }

      const accountToggleButton = document.querySelector(
        "[data-ui='site-header-action-local-account']",
      );
      const navbarWrapper = document.querySelector("[data-ui='site-navigation']");

      // Mount account and set event listeners, this function will only be executed if the account
      // button is hover or focused. Otherwise there is no need to mount component
      const mountAccount = () => {
        isAccountMounted = true;
        // Insert <li> element next to account toggle button to render Account component
        accountToggleButton.parentNode.insertAdjacentHTML(
          'afterEnd',
          `<li class="account-dropdown ${
            isWhiteNav && 'account-dropdown--white-nav'
          }"><div id="account-placement"></div></li>`,
        );
        createApp(AccountFlyoutComponent, {
          user,
          expiringRewardsExperience,
          expiringRewards,
          loginNonMemberExperience,
          memberVariation,
          nonMemberVariation,
        }).mount('#account-placement');
      };

      const signInLink = document.querySelector("[data-ui='login-module-greeting']");
      const mountSignInNudge = () => {
        isSignInNudgeMounted = true;

        signInLink.insertAdjacentHTML(
          'afterEnd',
          `<li class="sign-in-nudge-dropdown ${
            isWhiteNav && 'sign-in-nudge--white-nav'
          }"><div id="sign-in-nudge-placement"></div></li>`,
        );

        createApp(SignInNudgeComponent, { user, gugoExperience }).mount('#sign-in-nudge-placement');
      };

      if (accountToggleButton && navbarWrapper && isWhiteNav) {
        ['focus', 'mouseenter'].forEach((eventName) =>
          accountToggleButton.addEventListener(
            eventName,
            () => {
              if (!isAccountMounted) {
                mountAccount();
              }
            },
            {
              once: true,
            },
          ),
        );
        if (!isAccountMounted) {
          mountAccount();
        }
      }

      if (signInLink && navbarWrapper && isWhiteNav) {
        setTimeout(() => {
          if (!isSignInNudgeMounted) {
            mountSignInNudge();
          }
        }, 2000);
      }
    })
    .catch(() => {
      createApp(AccountLink).mount('[data-ui="login-module-greeting"]');
    });
}
